import React, { useEffect, useRef, useState } from "react";
import { FaSpinner, FaTimes, FaArrowLeft } from "react-icons/fa";
import { getRequest } from "../helpers/api.helper";
import { useLocation, useNavigate } from "react-router-dom";
import { UPLOAD_URL } from "../utils/constants";
import { useLanguage } from "../context/LanguageContext";

interface Package {
  id: string;
  name: string;
  price: number;
  validity: string;
  features: string[];
  highlight?: boolean;
}

const MembershipPlans: React.FC = () => {
  const [plans, setPlans] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
const { t } = useLanguage();
  const [showPopup, setShowPopup] = useState(false);
  const hasShownPopup = useRef(false);

  useEffect(() => {
    if (location.state?.fromShare && !hasShownPopup.current) {
      setShowPopup(true);
      hasShownPopup.current = true;
    }
  }, [location.state]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await getRequest("/admin/packages");
        if (result.success && Array.isArray(result.data)) {
          setPlans(result.data);
        } else {
          setError("Failed to load membership plans");
        }
      } catch (err) {
        console.error("Error fetching packages:", err);
        setError("Failed to load membership plans");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // ✅ Modal Component
  const Modal = showPopup && (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4">
      <div className="relative bg-white w-full max-w-md rounded-2xl shadow-xl p-6 text-center">
        {/* Close button */}
        <button
          onClick={() => setShowPopup(false)}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition"
          aria-label="Close"
        >
          <FaTimes className="w-5 h-5" />
        </button>

        <div className="w-14 h-14 mx-auto mb-4 bg-yellow-100 rounded-full flex items-center justify-center">
          <svg
            className="w-8 h-8 text-yellow-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4a2 2 0 00-3.464 0L4.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        <h2 className="text-xl font-bold text-gray-800">Premium Feature</h2>
        <p className="text-gray-600 text-sm mt-2 mb-4">
          This feature is available for paid members only. Upgrade your plan to
          access all premium features including:
        </p>

        <div className="text-left space-y-2 mb-6">
          {[
            "How to Use Guide",
            "Share Website Feature",
            "Digital Visiting Cards",
            "Advanced Analytics",
            "Priority Support",
          ].map((f, i) => (
            <div
              key={i}
              className="flex items-center gap-2 text-gray-700 text-sm"
            >
              <svg
                className="w-4 h-4 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              {f}
            </div>
          ))}
        </div>

        <div className="flex gap-4 justify-center mt-2">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 py-2 rounded-lg bg-gray-200 text-gray-700 font-medium hover:bg-gray-300 transition"
          >
            Maybe Later
          </button>

          <button
            onClick={() => setShowPopup(false)}
            className="flex-1 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium hover:opacity-90 transition"
          >
            Upgrade Now
          </button>
        </div>
      </div>
    </div>
  );

  const PlanCard = ({ plan }: { plan: any }) => {
    const [loading, setLoading] = useState(false);

    const handlePayNow = () => {
      setLoading(true);
      const waUrl = `https://wa.me/+919859242242?text=Hi,%20I%20want%20to%20purchase%20the%20${plan.name}%20plan`;
      setTimeout(() => {
        window.open(waUrl, "_blank");
        setLoading(false);
      }, 700);
    };

    return (
      <div
        className={`relative bg-white border rounded-2xl shadow-md hover:shadow-xl transition p-4 w-full ${
          plan.highlight
            ? "border-red-500 ring-2 ring-red-200"
            : "border-gray-200"
        }`}
      >
        <div className="w-full text-center mb-3 pb-2 border-b border-gray-200">
          <h3 className="text-2xl font-extrabold text-gray-800">
            {plan.title || "Gold"}
          </h3>
        </div>

        <div className="relative w-full" style={{ paddingTop: "133.33%" }}>
          <img
            src={`${UPLOAD_URL}/business/packages/${plan.image_path}`}
            alt={plan.name}
            className="absolute inset-0 w-full h-full object-contain rounded-xl"
          />
        </div>

        <button
          onClick={handlePayNow}
          disabled={loading}
          className={`w-full text-white text-lg font-semibold py-3 rounded-lg mt-4 transition flex items-center justify-center 
          ${
            loading
              ? "bg-orange-400 cursor-not-allowed"
              : "bg-orange-500 hover:bg-orange-600"
          }`}
        >
          {loading ? (
            <FaSpinner className="animate-spin text-white text-xl" />
          ) : (
            "Pay Now"
          )}
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white flex items-center justify-center">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-pink-500 mx-auto mb-4" />
            <p className="text-gray-600">Loading upgrade plans...</p>
          </div>
        </div>
        {Modal}
      </>
    );
  }

  if (error || plans.length === 0) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white pb-10 px-4 pt-24">
          <div className="max-w-md mx-auto text-center mt-20">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                No Plans Available
              </h3>
              <p className="text-gray-600 mb-6">
                There are no Upgrade plans available at the moment. Please check
                back later.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg font-semibold transition"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
        {Modal}
      </>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white pb-10 px-4 pt-5">
        {/* ✅ Back Button */}
        <div className="max-w-6xl mx-auto mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg font-medium transition"
          >
            <FaArrowLeft className="w-4 h-4" /> {t("Back")}
          </button>
        </div>

        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-800">
            🎉 {t("Membership Plans")} 🎉
          </h2>
          <p className="text-gray-600 mt-2">{t("Choose the perfect plan for you")}</p>
        </div>

        <div className="md:hidden">
          {plans.length === 1 ? (
            <div className="flex justify-center px-4">
              <div className="w-full max-w-sm">
                <PlanCard plan={plans[0]} />
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto scroll-smooth no-scrollbar">
              <div className="flex gap-4 snap-x snap-mandatory px-2">
                {plans.map((plan) => (
                  <div key={plan.id} className="snap-center min-w-[85%]">
                    <PlanCard plan={plan} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:grid grid-cols-3 gap-6 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>
      </div>
      {Modal}
    </>
  );
};

export default MembershipPlans;
